import { createInjectionState, useDebounce } from '@vueuse/core'
import axios from 'axios'
import { computed, ref, shallowRef, watch } from 'vue'

const [useProvideTeamAssets, _useTeamAssets] = createInjectionState((category) => {
  const teamAssets = shallowRef([])
  const teamAssetSelected = shallowRef()

  const teamAssetSharingFormRef = ref(null)

  const teamAssetsFilterTitle = ref('')
  const teamAssetsFilterUserId = ref('')
  const teamAssetsFilterTitleDebounced = useDebounce(teamAssetsFilterTitle, 500)

  const teamAssetsFilter = computed(() => {
    return {
      title: teamAssetsFilterTitleDebounced.value,
      user_id: teamAssetsFilterUserId.value,
      category: category.value,
    }
  })

  const isLoading = ref(false)
  const nextLink = ref(null)
  const canFetchNextPage = computed(() => nextLink.value !== null)

  const prependTeamAsset = (teamAsset) => {
    teamAssets.value = [
      teamAsset,
      ...teamAssets.value,
    ]

    teamAssetSelected.value = teamAsset
  }

  /**
   * @param {{hash_id: string}} teamAsset
   * @param {{any}} data
   * @returns {Promise<void>}
   */
  const updateTeamAsset = async (teamAsset, data) => {
    try {
      const res = await axios.patch(
        Spark.router('api.team.asset.patch', { asset: teamAsset.hash_id }),
        data,
      )

      const teamAssetNew = res.data

      teamAssets.value = teamAssets.value.map((asset) => {
        if (teamAssetNew.hash_id === asset.hash_id) {
          return teamAssetNew
        }

        return asset
      })
    }
    catch (err) {
      await Promise.reject(err)
    }
  }

  const deleteTeamAsset = async (teamAsset, options = {
    successText: 'Your team asset have been deleted.',
  }) => {
    const res = await window.SwalConfirm.fire({
      icon: 'warning',
      title: 'Are you sure?',
      confirmButtonText: 'Yes, delete!',
      preConfirm: () => axios.delete(
        Spark.router('api.team.asset.destroy', { asset: teamAsset.hash_id }),
      ),
    })

    if (res.isConfirmed) {
      teamAssets.value = teamAssets.value.filter(asset =>
        asset.hash_id !== teamAsset.hash_id,
      )

      if (teamAssetSelected.value?.hash_id === teamAsset.hash_id) {
        teamAssetSelected.value = undefined
      }

      await window.SwalTimer.fire({
        icon: 'success',
        title: 'Deleted!',
        text: options.successText,
      })
    }
  }

  const fetchTeamAssets = async () => {
    try {
      teamAssets.value = []
      isLoading.value = true

      const url = Spark.router('api.team.asset.index', {
        filter: teamAssetsFilter.value,
        limit: 12,
      })
      const res = await axios.get(url)

      teamAssets.value = res.data.data
      nextLink.value = res.data.links.next
    }
    catch (e) {
      console.error(e)
    }
    finally {
      isLoading.value = false
    }
  }

  const fetchNextPage = async () => {
    try {
      isLoading.value = true

      const url = nextLink.value
      nextLink.value = null

      const res = await axios.get(url)

      teamAssets.value = [
        ...teamAssets.value,
        ...res.data.data,
      ]

      nextLink.value = res.data.links.next
    }
    catch (e) {
      console.error(e)
    }
    finally {
      isLoading.value = false
    }
  }

  watch(teamAssetsFilter, () => {
    fetchTeamAssets()
  }, {
    immediate: true,
  })

  return {
    teamAssets,
    teamAssetSelected,
    category,
    teamAssetSharingFormRef,
    teamAssetsFilterTitle,
    teamAssetsFilterUserId,
    teamAssetsFilter,

    prependTeamAsset,
    updateTeamAsset,
    deleteTeamAsset,

    fetchTeamAssets,
    fetchNextPage,
    canFetchNextPage,
    isLoading,
  }
})

function useTeamAssets() {
  const res = _useTeamAssets()
  if (!res) {
    throw new Error('useTeamAssets() is called without provider.')
  }
  return res
}

export {
  useProvideTeamAssets,
  useTeamAssets,
}
